import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Save, User, BookOpen, Loader2, Banknote, Camera } from "lucide-react";
import { collection, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { db } from "@/firebase/config";
import { useToast } from "@/components/ui/use-toast";
import imageCompression from 'browser-image-compression';

// Define interfaces with strict typing
interface Subject {
  subjectId: string;
  name: string;
  code: string;
  type: "Core" | "Elective";
}

interface Student {
  id: string;
  registrationNumber: string;
  firstName: string;
  lastName: string;
  grade: string;
  // class/classUid may be null when not assigned — allow nullable to reflect runtime usage
  class?: string | null;
  classUid?: string | null;
  dormitory: string;
  status: string;
  gender: string;
  attendance: number;
  middleName?: string;
  behaviorScore?: number;
  dateOfBirth?: string;
  parentName?: string;
  parentPhone?: string;
  medicalInfo?: string;
  enrolledSubjects?: string[];
  photoUrl?: string | null;
  studentType?: string;
}

interface ClassItem {
  id: string;
  name: string;
  classUid: string;
  level: number;
  stream: string;
  capacity?: number;
}

interface Grade {
  grade: string;
  level: number;
  conventionId: string;
  isTerminal: boolean;
  nextGrade?: string;
  bridgeToGrade?: string;
  bridgeToConvention?: string;
  isGraduated: boolean;
  streams: Stream[];
}

interface Stream {
  id: string;
  classUid: string;
  name: string;
  roomNumber: string;
  capacity: number;
  enrolled: number;
  teacher: string | null;
  teacherId: string | null;
  currentClassTeacherUid: string | null;
  isGraduated: boolean;
}

interface StudentEditFormProps {
  student: Student | null;
  onBack: () => void;
  // Use a permissive type for onSave to avoid cross-file Student interface mismatches
  onSave: (updatedStudent: any) => void;
  schoolID: string;
  isVolunteer?: boolean;
  volunteerName?: string;
}

const StudentEditForm = ({ student, onBack, onSave, schoolID, isVolunteer = false, volunteerName }: StudentEditFormProps) => {
    // Debug: Log incoming student data
    // console.log("📂 [EditForm] Initializing with student:", student);
    // console.log("📷 [EditForm] Incoming photoUrl:", student?.photoUrl);
    
    const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(student?.photoUrl || null);
    const [imageLoading, setImageLoading] = useState<boolean>(!!student?.photoUrl);
    const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  // Use a flexible formData type to avoid strict cross-file Student type coupling
  const [formData, setFormData] = useState<any>(student);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [classHierarchy, setClassHierarchy] = useState<Grade[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dormitories, setDormitories] = useState<string[]>([]);
  const [selectedGradeFilter, setSelectedGradeFilter] = useState<string>(student?.grade || "");
  // const [schoolID, setSchoolID] = useState(""); // Using prop instead
  // const [hasValidClaims, setHasValidClaims] = useState(false); // Assumed valid if schoolID passed
  const [schoolType, setSchoolType] = useState<string>(""); // For checking if school is Mixed
  const [studentType, setStudentType] = useState<string>(""); // "Boarding" or "Day School"
  
  // New/Ongoing student fields
  const [isOngoingStudent, setIsOngoingStudent] = useState<boolean>(false);
  const [admissionDate, setAdmissionDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [admissionYear, setAdmissionYear] = useState<string>(new Date().getFullYear().toString());
  const [outstandingBalance, setOutstandingBalance] = useState<string>("0");

  // Update photo preview when student data changes
  useEffect(() => {
    if (student && (student as any).photoUrl && !profilePhoto) {
      const url = (student as any).photoUrl;
      if (url !== photoPreview) {
        setPhotoPreview(url);
        setImageLoading(true);
      }
    }
  }, [student, profilePhoto]);

  // Auth check is handled by parent component, but we can keep a simple check if needed.
  // For now, we rely on schoolID prop.

  // Fetch school type and initialize student type from existing data
  useEffect(() => {
    if (!schoolID) return;

    const fetchSchoolType = async () => {
      try {
        const schoolDetailsQuery = query(
          collection(db, `schools/${schoolID}/schoolDetails`),
          where("__name__", "==", "main")
        );
        const schoolDetailsSnapshot = await getDocs(schoolDetailsQuery);
        
        if (!schoolDetailsSnapshot.empty) {
          const schoolData = schoolDetailsSnapshot.docs[0].data();
          setSchoolType(schoolData.type || "");
        }
      } catch (error) {
        console.error("Error fetching school type:", error);
      }
    };

    fetchSchoolType();

    // Initialize studentType from existing student data
    console.log("🔍 [Init] Student prop received:", student);
    console.log("🔍 [Init] Student studentType field:", student ? (student as any).studentType : "no student");
    if (student && (student as any).studentType) {
      console.log("✅ [Init] Setting studentType to:", (student as any).studentType);
      setStudentType((student as any).studentType);
    } else {
      console.log("⚠️ [Init] No studentType found in student data");
    }
    
    // Initialize ongoing student fields from existing data
    if (student) {
      const s = student as any;
      setIsOngoingStudent(!!s.ongoing);
      if (s.admissionDate) {
        setAdmissionDate(s.admissionDate);
      }
      if (s.admissionYear) {
        setAdmissionYear(s.admissionYear.toString());
      }
      if (s.outstandingBalance !== undefined) {
        setOutstandingBalance(s.outstandingBalance.toString());
      }
    }
  }, [schoolID, student]);

  // Fetch dormitories
  useEffect(() => {
    if (!schoolID) return;

    const dormsRef = collection(db, `schools/${schoolID}/dorms`);
    const unsubscribe = onSnapshot(dormsRef, (snapshot) => {
      const dormNames = snapshot.docs.map(doc => doc.data().name as string).sort();
      setDormitories(dormNames);
    }, (error) => {
      console.error("Error fetching dorms:", error);
      toast({
        title: "Error Loading Dormitories",
        description: "Failed to fetch dormitories.",
        variant: "destructive",
        duration: 5000,
      });
    });

    return () => unsubscribe();
  }, [schoolID, toast]);

  // Fetch subjects
  useEffect(() => {
    if (!schoolID) return;

    const subjectsRef = collection(db, `schools/${schoolID}/subjects`);
    const unsubscribe = onSnapshot(subjectsRef, (snapshot) => {
      const subjectsData: Subject[] = snapshot.docs.map((doc) => ({
        subjectId: doc.id,
        name: doc.data().name as string,
        code: doc.data().code as string,
        type: doc.data().type as "Core" | "Elective",
      }));
      setSubjects(subjectsData);
    }, (error) => {
      console.error("Error fetching subjects:", error);
      toast({
        title: "Error Loading Subjects",
        description: "Failed to fetch subjects. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    });

    return () => unsubscribe();
  }, [schoolID, toast]);

  // Fetch classes exactly as in TeacherGrading.tsx
  useEffect(() => {
    if (!schoolID) return;

    const fetchClasses = async () => {
      try {
        const classesCollection = collection(db, `schools/${schoolID}/classes`);
        const classesSnapshot = await getDocs(classesCollection);
        const classesData: ClassItem[] = classesSnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name as string,
          classUid: doc.data().classUid || doc.id, // Fallback to doc.id if classUid not set
          level: doc.data().level as number,
          stream: doc.data().stream as string,
          capacity: (doc.data().capacity as number) || 0,
        })).filter(c => c.name && c.name.trim() !== "");

        console.log('Fetched classes:', classesData); // Debug log

        // Derive forms (class levels) as in TeacherGrading.tsx
        const extractFormLevel = (className: string): string => {
          const parts = className.trim().toLowerCase().split(/\s+/);
          if (parts.length >= 3 && parts[0] === 'pre' && parts[1] === 'primary') {
            return className.split(/\s+/).slice(0, 3).join(' ');
          } else if (parts.length >= 2 && parts[0] === 'form') {
            return className.split(/\s+/).slice(0, 2).join(' ');
          } else {
            return className.split(/\s+/).slice(0, 1).join(' ');
          }
        };

        const uniqueForms = [...new Set(classesData.map(c => extractFormLevel(c.name)))].sort((a, b) => a.localeCompare(b));
        console.log('Derived forms:', uniqueForms); // Debug log

        // Transform classesData into classHierarchy for UI
        const hierarchy: Grade[] = [];
        classesData.forEach((classItem) => {
          const gradeName = extractFormLevel(classItem.name);
          let grade = hierarchy.find((g) => g.grade === gradeName);
          if (!grade) {
            grade = {
              grade: gradeName,
              level: classItem.level,
              conventionId: classItem.name.split(' ')[0], // e.g., "Form" or "Pre"
              isTerminal: false, // Simplified, as stageType not available
              isGraduated: false,
              streams: [],
            };
            hierarchy.push(grade);
          }

          grade.streams.push({
            id: classItem.id,
            classUid: classItem.classUid,
            name: classItem.stream,
            roomNumber: "", // Not available in ClassItem, default empty
            capacity: 0, // Not available in ClassItem, default 0
            enrolled: 0, // Will be updated by fetchEnrollments
            teacher: null,
            teacherId: null,
            currentClassTeacherUid: null,
            isGraduated: false,
          });
        });

        // Fetch enrollment counts
        const classUids = classesData.map(c => c.classUid);
        const enrollmentCounts = await fetchEnrollments(classUids, schoolID);
        const updatedHierarchy = hierarchy.map(grade => ({
          ...grade,
          streams: grade.streams.map(stream => ({
            ...stream,
            enrolled: enrollmentCounts[stream.classUid] || 0,
            capacity: classesData.find(c => c.classUid === stream.classUid)?.capacity || 0,
          })),
        }));

        setClassHierarchy(updatedHierarchy.sort((a, b) => a.level - b.level));
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching classes:", error);
        toast({
          title: "Error Loading Classes",
          description: "Failed to fetch class data. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
      }
    };

    fetchClasses();
  }, [schoolID, toast]);

  // Fetch enrollment counts
  const fetchEnrollments = async (classUids: string[], schoolId: string): Promise<{ [key: string]: number }> => {
    if (classUids.length === 0) return {};
    const studentsRef = collection(db, `schools/${schoolId}/students`);
    const results: { [key: string]: number } = {};
    classUids.forEach(uid => results[uid] = 0);

    for (let i = 0; i < classUids.length; i += 10) {
      const batchUids = classUids.slice(i, i + 10);
      const batchQuery = query(studentsRef, where("currentClassUid", "in", batchUids));
      const snapshot = await getDocs(batchQuery);
      snapshot.forEach(doc => {
        const classUid = doc.data().currentClassUid as string | undefined;
        if (classUid) {
          results[classUid] = (results[classUid] || 0) + 1;
        }
      });
    }
    return results;
  };

  // Initialize selected subjects from student data and ensure Core subjects are selected
  useEffect(() => {
    if (student && subjects.length > 0) {
      setFormData(student);
      
      const coreSubjectIds = subjects.filter(s => s.type === "Core").map(s => s.subjectId);
      const existingEnrollments = student.enrolledSubjects ?? [];
      // Union of existing and core subjects
      const combinedSubjects = Array.from(new Set([...existingEnrollments, ...coreSubjectIds]));
      
      setSelectedSubjects(combinedSubjects);
    }
  }, [student, subjects]);

  const handleInputChange = (field: keyof Student, value: string) => {
    setFormData((prev: any) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleClassChange = (value: string) => {
    const [grade, streamName, classUid] = value.split("|");
    setFormData((prev: any) => (prev ? {
      ...prev,
      grade,
      class: streamName,
      classUid
    } : prev));
  };

  const handleSubjectToggle = (subjectId: string) => {
    setSelectedSubjects(prev =>
      prev.includes(subjectId)
        ? prev.filter(id => id !== subjectId)
        : [...prev, subjectId]
    );
  };

  const cleanStudentData = (data: any): any => {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined) {
        cleaned[key] = value;
      }
    }
    return cleaned;
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      console.log("❌ [Photo] No file selected");
      return;
    }

    console.log("📷 [Photo] File selected:", file.name, "Size:", (file.size / 1024).toFixed(1), "KB", "Type:", file.type);

    toast({
      title: "Processing Image",
      description: "Optimizing photo for storage...",
      duration: 2000,
    });

    const options = {
      maxSizeMB: 0.5, // Increased from 0.1 - less aggressive compression
      maxWidthOrHeight: 1280,
      useWebWorker: true,
      fileType: 'image/jpeg' as string,
    };

    try {
      console.log("🔄 [Photo] Starting compression with options:", options);
      const compressedFile = await imageCompression(file, options);
      console.log("✅ [Photo] Compression complete:", compressedFile.name, "Size:", (compressedFile.size / 1024).toFixed(1), "KB");
      
      const fileWithNewName = new File([compressedFile], file.name, {
        type: compressedFile.type,
      });
      
      setProfilePhoto(fileWithNewName);
      const previewUrl = URL.createObjectURL(fileWithNewName);
      console.log("🖼️ [Photo] Preview URL created:", previewUrl.substring(0, 50) + "...");
      setPhotoPreview(previewUrl);
      setImageLoading(false);
      
      toast({
        title: "Photo Ready",
        description: `Reduced size by ${Math.round((1 - compressedFile.size / file.size) * 100)}%`,
        duration: 3000,
      });
    } catch (error) {
      console.error("❌ [Photo] Compression error:", error);
      // Fallback: use original file without compression
      setProfilePhoto(file);
      const previewUrl = URL.createObjectURL(file);
      console.log("⚠️ [Photo] Using original file, preview URL:", previewUrl.substring(0, 50) + "...");
      setPhotoPreview(previewUrl);
      setImageLoading(false);
      toast({
        title: "Optimization Failed",
        description: "Photo was loaded without compression.",
        variant: "destructive",
      });
    }
  };

  const triggerCamera = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // ...existing code...
    if (schoolType === "Mixed (Day & Boarding)" && !studentType) {
      toast({
        title: "Student Type Required",
        description: "Please select whether this is a boarding or day school student.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }
    if (studentType === "Boarding" && !formData.dormitory) {
      toast({
        title: "Dormitory Required",
        description: "Dormitory is required for boarding students.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }
    if (formData && schoolID) {
      const updatedStudentWithSubjects: Student = {
        ...formData,
        enrolledSubjects: selectedSubjects,
        classUid: formData.classUid ?? null,
        class: formData.class ?? null,
      };
      const cleanedStudent = cleanStudentData(updatedStudentWithSubjects);
        try {
          const updateData: any = {
            ...cleanedStudent,
            currentClassUid: cleanedStudent.classUid ?? null,
            className: cleanedStudent.class ? `${cleanedStudent.grade} ${cleanedStudent.class}` : null,
          };
        // Always save studentType if it has a value - not just for Mixed schools
        if (studentType) {
          updateData.studentType = studentType;
        }
        console.log("📝 [Edit] Saving student data:", { studentType, updateDataStudentType: updateData.studentType });
        // Handle profile photo upload if changed
        if (profilePhoto) {
          console.log("📸 [Edit] Uploading profile photo:", profilePhoto.name, profilePhoto.size, "bytes");
          try {
            const studentUid = cleanedStudent.id;
            const photoRefPath = `schools/${schoolID}/students/${studentUid}/photo_${profilePhoto.name}`;
            console.log("📸 [Edit] Storage path:", photoRefPath);
            const { ref: storageRef, uploadBytes, getDownloadURL } = await import("firebase/storage");
            const storage = (await import("@/firebase/config")).storage;
            console.log("📸 [Edit] Storage instance obtained");
            const photoRef = storageRef(storage, photoRefPath);
            console.log("📸 [Edit] Starting uploadBytes...");
            await uploadBytes(photoRef, profilePhoto);
            console.log("📸 [Edit] uploadBytes complete, getting download URL...");
            const photoUrl = await getDownloadURL(photoRef);
            updateData.photoUrl = photoUrl;
            console.log("✅ [Edit] Photo uploaded successfully, URL:", photoUrl);
          } catch (uploadError) {
            console.error("❌ [Edit] Photo upload FAILED:", uploadError);
            toast({
              variant: "destructive",
              title: "Photo Upload Failed",
              description: String(uploadError),
            });
            // Continue without photo - don't block the rest of the update
          }
        } else {
          console.log("⚠️ [Edit] No new profile photo selected");
        }
        // Add ongoing/new student fields to update data
        if (isOngoingStudent) {
          updateData.ongoing = true;
          updateData.admissionDate = `${admissionYear}-01-01`; // Set to January 1st of selected year
          updateData.outstandingBalance = parseFloat(outstandingBalance) || 0;
          // Clear legacy admissionYear field if it exists
          updateData.admissionYear = null;
        } else {
          updateData.ongoing = false;
          updateData.admissionDate = admissionDate;
          // Clear ongoing-specific fields if switching to new
          updateData.outstandingBalance = null;
          updateData.admissionYear = null;
        }
        console.log("📝 [Edit] Final updateData ready for parent:", JSON.stringify(updateData, null, 2));
        console.log("📝 [Edit] photoUrl in updateData:", updateData.photoUrl);
        
        // REMOVED: await updateDoc(studentRef, updateData); 
        // Preventing double-write. We now delegate the ACTUAL save to the parent onSave handler
        // which calls handleUpdateStudent in StudentsManagement.
        
        // Pass the COMPLETE updateData (which includes photoUrl if uploaded)
        const finalStudentData = { ...cleanedStudent, ...updateData };
        console.log("🚀 [Edit] Calling onSave with final data...");
        await onSave(finalStudentData);
        
        toast({
          title: "Success",
          description: "Student updated successfully.",
        });
        onBack();
      } catch (error) {
        console.error("❌ [Edit] Error preparing updates:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to update student.",
        });
      }
    } else {
      console.error("Missing formData or schoolID", { formData, schoolID });
      toast({
        title: "Error",
        description: "Missing required data (School ID or Form Data). Please refresh and try again.",
        variant: "destructive",
      });
    }
  };

  // Extract grades and classes from classHierarchy
  const grades = classHierarchy.map(grade => grade.grade);
  const classes = classHierarchy.flatMap(grade =>
    grade.streams.map(stream => ({
      grade: grade.grade,
      stream: stream.name,
      classUid: stream.classUid,
      display: `${grade.grade} ${stream.name}`,
    }))
  );

  if (!student || !formData || isLoading) {
    return (
      <div className="flex justify-center items-center ">
        <div className="text-center text-gray-600 dark:text-gray-400">
          <p>Loading student data...</p>
          <Button variant="ghost" onClick={onBack} className="mt-4 flex items-center gap-2 mx-auto">
            <ArrowLeft className="h-4 w-4" />
            Back to Students
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <div className="mb-6">
        <Button variant="ghost" onClick={onBack} className="mb-4 flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Students
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Edit Student Information</h1>
        <p className="text-gray-600 dark:text-gray-400">Modify student details and records</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
              <User className="h-5 w-5" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            {/* Editable Profile Photo Section */}
            <div className="flex flex-col items-center space-y-4 pb-6 border-b border-gray-200 dark:border-gray-700">
              <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 relative overflow-hidden">
                {photoPreview ? (
                  <>
                    {imageLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700 z-10">
                        <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" />
                      </div>
                    )}
                    <img 
                      src={photoPreview} 
                      alt="profile" 
                      className={`w-full h-full rounded-full object-cover transition-opacity duration-300 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                      onLoad={() => setImageLoading(false)}
                      onError={() => setImageLoading(false)}
                    />
                  </>
                ) : (
                  <User className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                )}
              </div>
              <div>
                <Label htmlFor="profilePhoto" className="text-gray-700 dark:text-gray-300">Profile Photo</Label>
                <div className="flex gap-2 mt-1">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={triggerCamera}
                    className="flex-1 items-center gap-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white"
                  >
                    <Camera className="h-4 w-4" />
                    Take Photo / Choose
                  </Button>
                  {photoPreview && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      onClick={() => {
                        setProfilePhoto(null);
                        setPhotoPreview(null);
                        // If it's the original photo, we might want to keep it or mark it for deletion
                        // But for now, let's just clear the local preview
                      }}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10"
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <input
                  id="profilePhoto"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  ref={fileInputRef}
                  onChange={handlePhotoChange}
                  className="hidden"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Photos are optimized to save space. On mobile, this will open the camera.</p>
              </div>
            </div>
            {/* ...existing code... */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-sm font-semibold text-gray-700 dark:text-gray-300">First Name</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange("firstName", e.target.value)}
                  className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="middleName" className="text-sm font-semibold text-gray-700 dark:text-gray-300">Middle Name</Label>
                <Input
                  id="middleName"
                  value={formData.middleName ?? ""}
                  onChange={(e) => handleInputChange("middleName", e.target.value)}
                  className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-sm font-semibold text-gray-700 dark:text-gray-300">Last Name</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange("lastName", e.target.value)}
                  className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dateOfBirth" className="text-sm font-semibold text-gray-700 dark:text-gray-300">Date of Birth</Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth ?? ""}
                  onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
                  className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gender" className="text-sm font-semibold text-gray-700 dark:text-gray-300">Gender</Label>
                <Select value={formData.gender} onValueChange={(value) => handleInputChange("gender", value)}>
                  <SelectTrigger className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    <SelectItem value="Male" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Male</SelectItem>
                    <SelectItem value="Female" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="grade" className="text-sm font-semibold text-gray-700 dark:text-gray-300">Grade (Filter)</Label>
                <Select value={selectedGradeFilter} onValueChange={(value) => setSelectedGradeFilter(value)}>
                  <SelectTrigger className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                    <SelectValue placeholder="Pick a grade" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    {grades.map((grade) => (
                      <SelectItem key={grade} value={grade} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{grade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="class" className="text-sm font-semibold text-gray-700 dark:text-gray-300">Stream / Section</Label>
                <Select value={`${formData.grade}|${formData.class ?? ''}|${formData.classUid ?? ''}`} onValueChange={handleClassChange}>
                  <SelectTrigger 
                    className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
                    disabled={!selectedGradeFilter}
                  >
                    <SelectValue placeholder="Select stream">
                      {formData.class || "Select stream"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    {classes
                      .filter(cls => !selectedGradeFilter || cls.grade === selectedGradeFilter)
                      .map((cls) => (
                        <SelectItem
                          key={`${cls.grade}-${cls.stream}`}
                          value={`${cls.grade}|${cls.stream}|${cls.classUid}`}
                          className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]"
                        >
                          {cls.stream}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              {schoolType === "Mixed (Day & Boarding)" && (
                <div className="space-y-2">
                  <Label htmlFor="studentType" className="text-sm font-semibold text-gray-700 dark:text-gray-300">Student Type *</Label>
                  <Select value={studentType} onValueChange={setStudentType}>
                    <SelectTrigger className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                      <SelectValue placeholder="Select student type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                      <SelectItem value="Boarding" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Boarding Student</SelectItem>
                      <SelectItem value="Day School" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Day School Student</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="dormitory" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Dormitory {studentType === "Boarding" || schoolType !== "Mixed (Day & Boarding)" ? "*" : "(Optional)"}
                </Label>
                <Select 
                  value={formData.dormitory} 
                  onValueChange={(value) => handleInputChange("dormitory", value)}
                  disabled={studentType === "Day School"}
                >
                  <SelectTrigger className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                    <SelectValue placeholder="Select dormitory" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    {dormitories.map((dorm) => (
                      <SelectItem key={dorm} value={dorm} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{dorm}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Admission & Billing Information Card */}
        <Card className={`border ${isOngoingStudent ? 'border-amber-300 dark:border-amber-700' : 'border-[#E2E8F0] dark:border-[#1E293B]'} bg-[#FFFFFF] dark:bg-[#020817]`}>
          <CardHeader className={isOngoingStudent ? 'bg-amber-50 dark:bg-amber-900/20' : ''}>
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
              <Banknote className="h-5 w-5" />
              Admission & Billing Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            {/* Student Type Toggle */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Student Registration Type</Label>
              <Select value={isOngoingStudent ? "ongoing" : "new"} onValueChange={(value) => setIsOngoingStudent(value === "ongoing")}>
                <SelectTrigger className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                  <SelectItem value="new" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">New Student</SelectItem>
                  <SelectItem value="ongoing" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Ongoing Student</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {isOngoingStudent 
                  ? "Ongoing students are those who were enrolled before this system was adopted" 
                  : "New students are registered fresh into this system"}
              </p>
            </div>

            {/* Conditional Fields based on student type */}
            {isOngoingStudent ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="space-y-2">
                  <Label htmlFor="admissionYear" className="text-sm font-semibold text-gray-700 dark:text-gray-300">Year Student Joined *</Label>
                  <Select value={admissionYear} onValueChange={setAdmissionYear}>
                    <SelectTrigger className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                      {[...Array(6)].map((_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <SelectItem key={year} value={year.toString()} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                            {year}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 dark:text-gray-400">The year this student first enrolled at the school</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="outstandingBalance" className="text-sm font-semibold text-gray-700 dark:text-gray-300">Outstanding Balance (KES)</Label>
                  <Input
                    id="outstandingBalance"
                    type="number"
                    min="0"
                    step="0.01"
                    value={outstandingBalance}
                    onChange={(e) => setOutstandingBalance(e.target.value)}
                    placeholder="0"
                    className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">Any unpaid fees from before the system was introduced</p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="admissionDate" className="text-sm font-semibold text-gray-700 dark:text-gray-300">Admission Date *</Label>
                <Input
                  id="admissionDate"
                  type="date"
                  value={admissionDate}
                  onChange={(e) => setAdmissionDate(e.target.value)}
                  className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">The date this student joined the school</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
              <BookOpen className="h-5 w-5" />
              Enrolled Subjects ({selectedSubjects.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-3 max-h-64 overflow-y-auto overflow-x-hidden">
              {subjects.map((subject) => (
                <div
                  key={subject.subjectId}
                  className="flex items-center gap-3 p-3 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg hover:bg-gray-50 dark:hover:bg-[#131E31] transition-colors"
                >
                  <Checkbox
                    id={subject.subjectId}
                    checked={selectedSubjects.includes(subject.subjectId)}
                    onCheckedChange={() => handleSubjectToggle(subject.subjectId)}
                    className="border-[#E2E8F0] dark:border-[#1E293B] data-[state=unchecked]:bg-white dark:data-[state=unchecked]:bg-[#020817] data-[state=checked]:bg-[#155DFC] dark:data-[state=checked]:bg-[#155DFC]"
                  />
                  <Label
                    htmlFor={subject.subjectId}
                    className="text-sm font-medium text-gray-900 dark:text-white cursor-pointer flex-1 min-w-0"
                  >
                    <div className="flex flex-col">
                      <span className="truncate">{subject.name}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{subject.code}</span>
                    </div>
                  </Label>
                  <Badge variant={subject.type === "Core" ? "default" : "secondary"} className="text-xs">
                    {subject.type}
                  </Badge>
                </div>
              ))}
              {subjects.length === 0 && (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  No subjects available. Please add subjects first.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-white">Parent/Guardian Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            <div className="space-y-2">
              <Label htmlFor="parentName" className="text-sm font-semibold text-gray-700 dark:text-gray-300">Parent/Guardian Name</Label>
              <Input
                id="parentName"
                value={formData.parentName ?? ""}
                onChange={(e) => handleInputChange("parentName", e.target.value)}
                className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="parentPhone" className="text-sm font-semibold text-gray-700 dark:text-gray-300">Phone Number</Label>
              <Input
                id="parentPhone"
                value={formData.parentPhone ?? ""}
                onChange={(e) => handleInputChange("parentPhone", e.target.value)}
                className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4 pt-6 border-t border-[#E2E8F0] dark:border-[#1E293B]">
          <Button type="button" variant="outline" onClick={onBack} className="bg-transparent border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31]">
            Cancel
          </Button>
          <Button type="submit" className="flex items-center ml-2 gap-2 bg-[#155DFC] text-white hover:bg-[#2563EB]">
            <Save className="h-4 w-4" />
            Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
};

export default StudentEditForm;